/**
 * PersonaOverviewForm - Reusable form for Persona overview fields
 * 
 * Used in:
 * - PersonaDetailModal (edit mode)
 * - PersonaMergePanel (source view + merged form)
 * 
 * Props:
 * - formData: { name, role, goals, pain_points, context }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states)
 * - colors: theme colors from useSafeMode
 * - errors: { [field]: string } - validation errors to display
 * - mode: 'edit' | 'create' | 'view'
 * - showLabelsInline: boolean - for compact layouts
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { Target, AlertTriangle, AlertCircle, User, Briefcase } from 'lucide-react';

// Required fields for persona
export const PERSONA_REQUIRED_FIELDS = ['name', 'role'];

// Field labels for display
export const PERSONA_FIELD_LABELS = {
  name: 'Name',
  role: 'Role',
  goals: 'Goals',
  pain_points: 'Pain Points',
  context: 'Context / Background',
};

const PersonaOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  errors = {},
  mode = 'edit',
  showLabelsInline = false,
}) => {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  // Check if field is required
  const isRequired = (field) => PERSONA_REQUIRED_FIELDS.includes(field);

  const effectiveDisabled = isDisabled || !isEditing;

  return (
    <div className="space-y-4">
      {/* Name & Role Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <Label className={cn(
            "flex items-center gap-1",
            colors?.textSecondary,
            errors.name && "text-red-500"
          )}>
            <User className="w-3 h-3" />
            {PERSONA_FIELD_LABELS.name}
            <span className="text-red-500">*</span>
          </Label>
          {isEditing && !isDisabled ? (
            <>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Enterprise Emma"
                disabled={effectiveDisabled}
                className={cn(
                  "mt-1",
                  colors?.bgInput,
                  colors?.borderPrimary,
                  colors?.textPrimary,
                  getFieldErrorClass('name')
                )}
                data-testid="persona-name-input"
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </p>
              )}
            </>
          ) : (
            <p className={cn("mt-1 text-lg font-medium", colors?.textPrimary)}>
              {formData.name || 'Unnamed Persona'}
            </p>
          )}
        </div>

        {/* Role */}
        <div>
          <Label className={cn(
            "flex items-center gap-1",
            colors?.textSecondary,
            errors.role && "text-red-500"
          )}>
            <Briefcase className="w-3 h-3" />
            {PERSONA_FIELD_LABELS.role}
            <span className="text-red-500">*</span>
          </Label>
          {isEditing && !isDisabled ? (
            <>
              <Input
                value={formData.role || ''}
                onChange={(e) => handleChange('role', e.target.value)}
                placeholder="e.g., IT Administrator at mid-sized B2B company"
                disabled={effectiveDisabled}
                className={cn(
                  "mt-1",
                  colors?.bgInput,
                  colors?.borderPrimary,
                  colors?.textPrimary,
                  getFieldErrorClass('role')
                )}
                data-testid="persona-role-input"
              />
              {errors.role && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.role}
                </p>
              )}
            </>
          ) : (
            <p className={cn("mt-1", colors?.textSecondary)}>
              {formData.role || 'No role defined'}
            </p>
          )}
        </div>
      </div>

      {/* Goals */}
      <div>
        <Label className={cn("flex items-center gap-1", colors?.textSecondary)}>
          <Target className="w-3 h-3" />
          {PERSONA_FIELD_LABELS.goals}
        </Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.goals || ''}
            onChange={(e) => handleChange('goals', e.target.value)}
            placeholder="What are this persona's primary goals and objectives?"
            rows={4}
            disabled={effectiveDisabled}
            className={cn(
              "mt-1 min-h-[100px]",
              colors?.bgInput,
              colors?.borderPrimary,
              colors?.textPrimary,
              getFieldErrorClass('goals')
            )}
            data-testid="persona-goals-input"
          />
        ) : (
          <div className={cn(
            "mt-1 p-3 rounded-lg min-h-[60px] whitespace-pre-wrap",
            colors?.bgTertiary
          )}>
            <p className={cn("text-sm", colors?.textSecondary)}>
              {formData.goals || <span className={colors?.textMuted}>No goals defined</span>}
            </p>
          </div>
        )}
      </div>

      {/* Pain Points */}
      <div>
        <Label className={cn("flex items-center gap-1", colors?.textSecondary)}>
          <AlertTriangle className="w-3 h-3" />
          {PERSONA_FIELD_LABELS.pain_points}
        </Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.pain_points || ''}
            onChange={(e) => handleChange('pain_points', e.target.value)}
            placeholder="What frustrates this persona? What challenges do they face?"
            rows={4}
            disabled={effectiveDisabled}
            className={cn(
              "mt-1 min-h-[100px]",
              colors?.bgInput,
              colors?.borderPrimary,
              colors?.textPrimary,
              getFieldErrorClass('pain_points')
            )}
            data-testid="persona-pain-points-input"
          />
        ) : (
          <div className={cn(
            "mt-1 p-3 rounded-lg min-h-[60px] whitespace-pre-wrap",
            colors?.bgTertiary
          )}>
            <p className={cn("text-sm", colors?.textSecondary)}>
              {formData.pain_points || <span className={colors?.textMuted}>No pain points defined</span>}
            </p>
          </div>
        )}
      </div>

      {/* Context / Background */}
      <div>
        <Label className={colors?.textSecondary}>
          {PERSONA_FIELD_LABELS.context}
        </Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.context || ''}
            onChange={(e) => handleChange('context', e.target.value)}
            placeholder="Additional context about this persona's work environment, company size, etc."
            rows={4}
            disabled={effectiveDisabled}
            className={cn(
              "mt-1 min-h-[100px]",
              colors?.bgInput,
              colors?.borderPrimary,
              colors?.textPrimary,
              getFieldErrorClass('context')
            )}
            data-testid="persona-context-input"
          />
        ) : (
          <div className={cn(
            "mt-1 p-3 rounded-lg min-h-[60px] whitespace-pre-wrap",
            colors?.bgTertiary
          )}>
            <p className={cn("text-sm", colors?.textSecondary)}>
              {formData.context || <span className={colors?.textMuted}>No context defined</span>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonaOverviewForm;
